// Safari's explicit capture category prevents an AudioContext from being
// inferred as an ambient/playback session during microphone activation.
// Unsupported browsers are untouched; the previous category is restored.
const leases = new WeakMap();

export function acquireMicrophoneSession(enabled) {
  if (!enabled) return () => {};
  try {
    const session = navigator.audioSession;
    if (!session || typeof session.type !== 'string') return () => {};
    let entry = leases.get(session);
    if (!entry) {
      entry = { previous: session.type, owners: new Set() };
      session.type = 'play-and-record';
      leases.set(session, entry);
    }
    const owner = {};
    entry.owners.add(owner);
    return () => {
      if (!entry.owners.delete(owner) || entry.owners.size) return;
      try { if (session.type === 'play-and-record') session.type = entry.previous; }
      catch { /* Restoring an optional browser feature cannot fail a save. */ }
      leases.delete(session);
    };
  } catch { return () => {}; }
}

export function microphoneRms(analyser, buffer) {
  const floats = buffer instanceof Float32Array;
  try {
    if (floats) analyser.getFloatTimeDomainData(buffer);
    else analyser.getByteTimeDomainData(buffer);
  } catch { return null; }
  let mean = 0;
  for (const sample of buffer) mean += floats ? sample : (sample - 128) / 128;
  mean /= buffer.length;
  let energy = 0;
  for (const sample of buffer) {
    const value = (floats ? sample : (sample - 128) / 128) - mean;
    energy += value * value;
  }
  const rms = Math.sqrt(energy / buffer.length);
  return Number.isFinite(rms) ? rms : null;
}

// Preserve the existing 1.8s end-of-speech pause, but recognize quiet input
// below the former fixed0.018 threshold. A short rolling history also avoids
// losing immediate speech while the initial noise estimate settles.
export function createSpeechPauseDetector(startedAt) {
  let history = [], heardSpeech = false, lastSpeech = startedAt;
  return {
    update(rms, now) {
      if (!Number.isFinite(rms) || rms < 0) return false;
      history.push({ rms, at: now });
      history = history.filter(frame => now - frame.at <= 1500);
      const sorted = history.map(frame => frame.rms).sort((a, b) => a - b);
      const noiseFloor = sorted[Math.floor((sorted.length - 1) * 0.2)] || 0;
      const threshold = Math.max(0.002, Math.min(0.018, noiseFloor * 1.8));
      const speech = history.filter(frame => frame.rms >= threshold);
      if (speech.length >= 2 && speech[speech.length - 1].at - speech[0].at >= 80) {
        heardSpeech = true;
        lastSpeech = Math.max(lastSpeech, speech[speech.length - 1].at);
      }
      return heardSpeech && now - startedAt >= 2000 && now - lastSpeech >= 1800;
    },
  };
}